import React, { useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BoxContext from '../context/BoxContext';

/**
 * Sidebar animado que muestra los productos en la caja con carrito completo.
 * Se desliza desde la derecha con animaciones suaves.
 * Incluye cantidades, precios, total y envío por WhatsApp.
 */
export default function BoxSidebar() {
    const { boxItems, boxCount, removeFromBox, clearBox, updateQuantity, getTotal, isBoxOpen, toggleBox } = useContext(BoxContext);

    // Función para formatear precio en pesos chilenos
    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    // Estados para el formulario de datos del comprador
    const [showForm, setShowForm] = React.useState(false);
    const [buyerData, setBuyerData] = React.useState({
        name: '',
        address: '',
        phone: ''
    });

    // Función para manejar cambios en el formulario
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBuyerData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Función para validar y enviar pedido por WhatsApp
    const handleSubmitOrder = () => {
        // Validar que todos los campos estén completos
        if (!buyerData.name.trim() || !buyerData.address.trim() || !buyerData.phone.trim()) {
            alert('Por favor, completa todos los campos del formulario');
            return;
        }

        const phoneNumber = '59892696366';

        // Mensaje simplificado para compatibilidad con WhatsApp Desktop
        let message = `Hola! Quiero hacer un pedido:%0A%0A`;
        message += `Nombre: ${buyerData.name}%0A`;
        message += `Direccion: ${buyerData.address}%0A`;
        message += `Telefono: ${buyerData.phone}%0A%0A`;

        message += `PRODUCTOS:%0A`;
        boxItems.forEach((item, index) => {
            const subtotal = item.price * item.quantity;
            message += `${index + 1}. ${item.name} x${item.quantity} - ${subtotal.toLocaleString('es-UY')} pesos%0A`;
        });

        const total = getTotal();
        message += `%0ATOTAL: ${total.toLocaleString('es-UY')} pesos`;

        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

        window.open(whatsappUrl, '_blank');

        // Limpiar formulario y cerrar
        setBuyerData({ name: '', address: '', phone: '' });
        setShowForm(false);
    }; return (
        <>
            {/* Overlay oscuro */}
            <AnimatePresence>
                {isBoxOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={toggleBox}
                        className="fixed inset-0 bg-black/50 z-40"
                    />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <AnimatePresence>
                {isBoxOpen && (
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col"
                    >
                        {/* Header del Sidebar */}
                        <div className="bg-primary text-white p-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-3xl">🛒</span>
                                <div>
                                    <h2 className="text-xl font-bold">Mi Carrito</h2>
                                    <p className="text-sm opacity-90">{boxCount} {boxCount === 1 ? 'producto' : 'productos'}</p>
                                </div>
                            </div>
                            <button
                                onClick={toggleBox}
                                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                                aria-label="Cerrar"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {boxCount === 0 ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="text-center py-12"
                                >
                                    <span className="text-6xl block mb-4">🛒</span>
                                    <p className="text-gray-500 text-lg">Tu carrito está vacío</p>
                                    <p className="text-gray-400 text-sm mt-2">Agrega productos para comenzar</p>
                                </motion.div>
                            ) : (
                                <div className="space-y-3">
                                    <AnimatePresence>
                                        {boxItems.map((item, index) => (
                                            <motion.div
                                                key={item.itemId}
                                                initial={{ opacity: 0, x: 50 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -50, height: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200"
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3 className="font-semibold text-primary text-base">{item.name}</h3>
                                                    <button
                                                        onClick={() => removeFromBox(item.itemId)}
                                                        className="text-red-500 hover:text-red-700 transition-colors"
                                                        aria-label="Eliminar"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-600 mb-2">{item.persons}</p>

                                                {/* Controles de cantidad y precio */}
                                                <div className="flex items-center justify-between mt-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => updateQuantity(item.itemId, item.quantity - 1)}
                                                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 w-8 h-8 rounded-full flex items-center justify-center transition-colors font-bold"
                                                            aria-label="Disminuir cantidad"
                                                        >
                                                            -
                                                        </button>
                                                        <span className="font-semibold text-lg w-8 text-center">{item.quantity}</span>
                                                        <button
                                                            onClick={() => updateQuantity(item.itemId, item.quantity + 1)}
                                                            className="bg-primary hover:bg-primary-dark text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors font-bold"
                                                            aria-label="Aumentar cantidad"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500">{formatPrice(item.price)} c/u</p>
                                                        <p className="font-bold text-primary">{formatPrice(item.price * item.quantity)}</p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>

                        {/* Footer con total y acciones */}
                        {boxCount > 0 && (
                            <motion.div
                                initial={{ y: 100 }}
                                animate={{ y: 0 }}
                                className="border-t border-gray-200 bg-white"
                            >
                                {/* Total */}
                                <div className="px-4 py-3 bg-gray-50">
                                    <div className="flex justify-between items-center">
                                        <span className="text-lg font-semibold text-gray-700">Total:</span>
                                        <span className="text-2xl font-bold text-primary">{formatPrice(getTotal())}</span>
                                    </div>
                                </div>

                                {/* Formulario de datos del comprador */}
                                {showForm && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="px-4 py-3 bg-gray-50 border-t border-gray-200"
                                    >
                                        <h3 className="font-semibold text-gray-700 mb-3">Datos del comprador:</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Nombre completo *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={buyerData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Ej: Juan Pérez"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Dirección de entrega *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="address"
                                                    value={buyerData.address}
                                                    onChange={handleInputChange}
                                                    placeholder="Ej: Av. Principal 123, Apto 4B"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Teléfono de contacto *
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={buyerData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="Ej: 099 123 456"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {/* Botones */}
                                <div className="p-4 space-y-2">
                                    {!showForm ? (
                                        <button
                                            onClick={() => setShowForm(true)}
                                            className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold transition-colors shadow-md flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                            </svg>
                                            Enviar Pedido por WhatsApp
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={handleSubmitOrder}
                                                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold transition-colors shadow-md flex items-center justify-center gap-2"
                                            >
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                </svg>
                                                Confirmar y Enviar
                                            </button>
                                            <button
                                                onClick={() => setShowForm(false)}
                                                className="w-full bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={clearBox}
                                        className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                                    >
                                        Vaciar Carrito
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
